import tenantConfig from './index'
import applySeo from './applySeo'
import type { TenantColors } from './types'

/** Brand title used for the PWA manifest and as the title fallback. */
function getBrandTitle(): string {
  const { name, tagline, seo } = tenantConfig
  return seo?.title ?? `${name} — ${tagline}`
}

/** Brand description used for the PWA manifest, derived from SEO/content. */
function getBrandDescription(): string {
  const { name, tagline, seo, content } = tenantConfig
  return seo?.description ?? content.heroDescription ?? `${name}. ${tagline}.`
}

/**
 * Converts camelCase to kebab-case for CSS variable names.
 */
function toKebabCase(str: string): string {
  return str.replaceAll(/([A-Z])/g, '-$1').toLowerCase()
}

function applyVarsToRoot(
  obj: Record<string, string>,
  root: HTMLElement,
  prefix = ''
): void {
  Object.entries(obj).forEach(([key, value]) => {
    const cssVar = `--${prefix}${toKebabCase(key)}`
    root.style.setProperty(cssVar, value)
  })
}

function applyColorVars(colors: TenantColors, root: HTMLElement): void {
  const colorMapping: Record<string, string> = {
    '--text-color': colors.textColorPrimary,
    '--text-color-primary': colors.textColorPrimary,
    '--text-color-secondary': colors.textColorSecondary,
    '--text-color-lighter': colors.textColorLighter,
    '--text-color-lighter-2': colors.textColorLighter2,
    '--text-color-lighter-3': colors.textColorLighter3,
    '--text-color-heading': colors.textColorHeading,
    '--text-color-content': colors.textColorContent,
    '--text-color-light': colors.textColorLight,
    '--text-color-helper': colors.textColorHelper,
    '--text-light-grey': colors.textLightGrey,
    '--text-dark-grey': colors.textDarkGrey,
    '--text-color-green': colors.textColorGreen,
    '--text-color-green-2': colors.textColorGreen2,
    '--text-color-red': colors.textColorRed,
    '--button-color-primary': colors.buttonColorPrimary,
    '--button-color-primary-gradient-start':
      colors.buttonColorPrimaryGradientStart,
    '--button-background-color-primary': colors.buttonBackgroundColorPrimary,
    '--button-background-color-subtle': colors.buttonBackgroundColorSubtle,
    '--button-background-color-lighter': colors.buttonBackgroundColorLighter,
    '--button-color-subtle': colors.buttonColorSubtle,
    '--button-color-primary-default': colors.buttonColorPrimaryDefault,
    '--button-border-color': colors.buttonBorderColor,
    '--background-color-white': colors.backgroundColorWhite,
    '--background-color-lighter': colors.backgroundColorLighter,
    '--background-color-lighter-200': colors.backgroundColorLighter200,
    '--background-color-darker': colors.backgroundColorDarker,
    '--background-color-darker-2': colors.backgroundColorDarker2,
    '--background-color-darker-3': colors.backgroundColorDarker3,
    '--background-color-lighter-red': colors.backgroundColorLighterRed,
    '--light-red-background-color': colors.lightRedBackgroundColor,
    '--dark-red-text-color': colors.darkRedTextColor,
    '--light-green-background-color': colors.lightGreenBackgroundColor,
    '--dark-green-text-color': colors.darkGreenTextColor,
    '--divider-color': colors.dividerColor,
    '--border-color': colors.borderColor,
    '--border-color-darker': colors.borderColorDarker,
    '--side-nav-shadow-color': colors.sideNavShadowColor,
    '--toolbar-border-bottom-color': colors.toolbarBorderBottomColor,
    '--dropdown-divider-color': colors.dropdownDividerColor
  }

  Object.entries(colorMapping).forEach(([cssVar, value]) => {
    root.style.setProperty(cssVar, value)
  })
}

function applyFavicon(): void {
  const { logo, name } = tenantConfig

  // Update all favicon links to use the tenant logo
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
    .forEach((link) => {
      link.setAttribute('href', logo)
    })

  // Update apple-touch-icon
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="apple-touch-icon"]')
    .forEach((link) => {
      link.setAttribute('href', logo)
    })

  // Update manifest dynamically with tenant info
  const manifestLink = document.querySelector<HTMLLinkElement>(
    'link[rel="manifest"]'
  )
  if (manifestLink) {
    const manifest = {
      short_name: name,
      name: getBrandTitle(),
      description: getBrandDescription(),
      icons: [
        {
          src: logo,
          sizes: '192x192',
          type: 'image/png'
        },
        {
          src: logo,
          sizes: '512x512',
          type: 'image/png'
        }
      ],
      start_url: '.',
      display: 'standalone',
      theme_color: tenantConfig.theme.emailPrimaryHex,
      background_color: '#ffffff'
    }
    const blob = new Blob([JSON.stringify(manifest)], {
      type: 'application/json'
    })
    manifestLink.setAttribute('href', URL.createObjectURL(blob))
  }
}

function applyThemeColor(): void {
  const themeColorMeta = document.querySelector('meta[name="theme-color"]')
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', tenantConfig.theme.emailPrimaryHex)
  }
}

export function applyTenantTheme(): void {
  const { theme, colors } = tenantConfig
  const root = document.documentElement

  // Apply HSL theme variables (design system)
  applyVarsToRoot(theme as unknown as Record<string, string>, root)

  // Apply color variables (SCSS variable replacements)
  applyColorVars(colors, root)

  // Map to RSuite primary palette using hsl() wrapped values
  root.style.setProperty('--rs-primary-50', `hsl(${theme.muted})`)
  root.style.setProperty('--rs-primary-100', `hsl(${theme.muted})`)
  root.style.setProperty('--rs-primary-500', `hsl(${theme.primary})`)
  root.style.setProperty('--rs-primary-600', `hsl(${theme.primary})`)
  root.style.setProperty('--rs-primary-700', `hsl(${theme.ring})`)
  root.style.setProperty('--rs-primary-800', `hsl(${theme.ring})`)
  root.style.setProperty('--rs-primary-900', `hsl(${theme.ring})`)

  // App-level overrides used by existing SCSS components
  root.style.setProperty('--header-logo-bg', `hsl(${theme.primary})`)
  root.style.setProperty('--sidenav-toggle-bg', `hsl(${theme.ring})`)
  root.style.setProperty(
    '--font-primary',
    "'Inter', -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  )

  root.style.setProperty(
    '--background-color-brand',
    tenantConfig.authBrandBackground
  )

  // SEO: title, meta description/keywords, Open Graph, Twitter, JSON-LD
  applySeo()

  // Dynamic favicon, manifest, and theme-color
  applyFavicon()
  applyThemeColor()
}

export function applyDarkTheme(): void {
  const { darkTheme } = tenantConfig
  const root = document.documentElement
  applyVarsToRoot(darkTheme as unknown as Record<string, string>, root)

  // Update RSuite vars for dark mode
  root.style.setProperty('--rs-primary-500', `hsl(${darkTheme.primary})`)
  root.style.setProperty('--rs-primary-700', `hsl(${darkTheme.ring})`)
  root.style.setProperty('--rs-primary-900', `hsl(${darkTheme.ring})`)
  root.style.setProperty('--header-logo-bg', `hsl(${darkTheme.primary})`)
  root.style.setProperty('--sidenav-toggle-bg', `hsl(${darkTheme.ring})`)
}

export function applyLightTheme(): void {
  applyTenantTheme()
}
